import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import PartnerInvite from "@/components/PartnerInvite";

const API = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e) => e.response?.data?.detail || "Errore";

async function openPdf(path, name) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch { toast.error("PDF non disponibile"); }
}

function PdfGenerator() {
  const [name, setName] = useState("");
  const url = `${API}/api/partner/proposal.pdf${name.trim() ? `?name=${encodeURIComponent(name.trim())}` : ""}`;
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid="admin-pdf-generator">
      <div className="font-serif text-lg text-ink mb-1">Proposta Partner personalizzata</div>
      <div className="text-sm text-stone mb-3">Scrivi il nome dell'azienda del gestore: comparirà in copertina.</div>
      <div className="flex flex-wrap gap-2">
        <Input className="max-w-sm" placeholder="Es. Rete Vacanze Italia S.r.l." value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-pdf-name" />
        <Button asChild className="rounded-full bg-ink text-canvas hover:bg-olive"><a href={url} target="_blank" rel="noreferrer" data-testid="admin-pdf-generate"><FilePdf size={16} className="mr-1" />Genera PDF</a></Button>
        <Button asChild variant="outline" className="rounded-full"><a href="/partner" target="_blank" rel="noreferrer" data-testid="admin-partners-page">Pagina pubblica Partner</a></Button>
      </div>
    </div>
  );
}

function ApprovedActions({ p, i, reload }) {
  const [comm, setComm] = useState(p.commission_pct ?? 20);
  const call = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (p.status === "revoked") return (
    <div className="flex flex-col items-end gap-2 text-sm">
      <span className="text-xs text-stone">Strutture attive fino al {p.properties_active_until ? new Date(p.properties_active_until).toLocaleDateString("it-IT") : "—"}</span>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => call(() => api.post(`/admin/partners/${p.id}/reactivate`), "Partner riattivato")} data-testid={`admin-partner-reactivate-${i}`}>Riattiva</Button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {p.model === "referral" && <>
        <label className="flex items-center gap-1">Comm. %<input type="number" className="w-14 border border-line rounded px-1 bg-canvas" value={comm} onChange={(e) => setComm(e.target.value)} data-testid={`admin-partner-comm-edit-${i}`} /></label>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => call(() => api.put(`/admin/partners/${p.id}`, { commission_pct: Number(comm) }), "Commissione aggiornata")} data-testid={`admin-partner-comm-save-${i}`}>Salva</Button>
      </>}
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => call(() => api.put(`/admin/partners/${p.id}`, { extend_pilot_days: 30 }), "Pilota esteso di 30 giorni")} data-testid={`admin-partner-extend-${i}`}>{p.pilot_until ? "Estendi pilota +30gg" : "Avvia pilota 30gg"}</Button>
      {p.pilot_until && <Button size="sm" variant="outline" className="rounded-full" onClick={() => call(() => api.put(`/admin/partners/${p.id}`, { end_pilot: true }), "Pilota terminato")} data-testid={`admin-partner-endpilot-${i}`}>Termina pilota</Button>}
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openPdf(`/admin/partners/${p.id}/report.pdf`, `Report-${p.company_name}.pdf`)} data-testid={`admin-partner-report-${i}`}><FilePdf size={14} className="mr-1" />Report</Button>
      <Button size="sm" variant="outline" className="rounded-full text-terracotta border-terracotta/40" onClick={() => window.confirm(`Revocare ${p.company_name}? Perde l'accesso alla Console; le strutture restano attive fino a fine mese.`) && call(() => api.post(`/admin/partners/${p.id}/revoke`), "Partner revocato")} data-testid={`admin-partner-revoke-${i}`}>Revoca</Button>
    </div>
  );
}

function PartnerRow({ p, i, reload }) {
  const [pilot, setPilot] = useState(true);
  const [comm, setComm] = useState(p.commission_pct ?? 20);
  const decide = async (approve) => {
    await api.post(`/admin/partners/${p.id}/decision`, { approve, pilot, commission_pct: Number(comm) });
    toast.success(approve ? "Partner approvato" : "Partner rifiutato"); reload();
  };
  return (
    <div className="py-4 border-b border-line last:border-0 flex flex-wrap gap-4 justify-between" data-testid={`admin-partner-row-${i}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2"><b className="text-ink">{p.company_name}</b><Badge variant="outline">{p.model_label}</Badge><Badge variant="outline" data-testid={`admin-partner-status-${i}`}>{p.status}</Badge></div>
        <div className="text-xs text-stone mt-1">{p.owner?.full_name} · {p.owner?.email} · {p.phone} · P.IVA {p.vat || "—"}</div>
        <div className="text-xs text-stone">Strutture stimate: {p.est_properties} · attive: {p.properties} · codice: {p.referral_code} · comm. {p.commission_pct}%{p.pilot_until ? ` · pilota fino al ${new Date(p.pilot_until).toLocaleDateString("it-IT")}` : ""}</div>
        {p.notes && <div className="text-xs text-ink/70 mt-1 max-w-xl">{p.notes}</div>}
      </div>
      {p.status === "pending" ? (
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1"><input type="checkbox" checked={pilot} onChange={(e) => setPilot(e.target.checked)} data-testid={`admin-partner-pilot-${i}`} />Pilota 60gg</label>
          {p.model === "referral" && <label className="flex items-center gap-1">Comm. %<input type="number" className="w-14 border border-line rounded px-1 bg-canvas" value={comm} onChange={(e) => setComm(e.target.value)} data-testid={`admin-partner-comm-${i}`} /></label>}
          <Button size="sm" className="rounded-full bg-olive text-canvas" onClick={() => decide(true)} data-testid={`admin-partner-approve-${i}`}>Approva</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(false)} data-testid={`admin-partner-reject-${i}`}>Rifiuta</Button>
        </div>
      ) : (p.status === "approved" || p.status === "revoked") && <ApprovedActions p={p} i={i} reload={reload} />}
    </div>
  );
}

export default function AdminPartners() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/partners").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const sendReports = async () => { try { const r = await api.post("/admin/partners/reports/send"); toast.success(`Report inviati: ${r.data.sent}`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="admin-partners">
      <PdfGenerator />
      <PartnerInvite />
      <div className="rounded-2xl border border-line bg-canvas p-5">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <div className="font-serif text-lg text-ink">Richieste e Partner</div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={sendReports} data-testid="admin-partners-send-reports">Invia ora i report del mese scorso</Button>
        </div>
        <div className="text-xs text-stone mb-2">I report mensili partono in automatico il 1° di ogni mese alle 8:00 (al Partner e in copia a te).</div>
        {rows === null ? <div className="text-stone">Caricamento…</div> : rows.length ? rows.map((p, i) => <PartnerRow key={p.id} p={p} i={i} reload={load} />)
          : <div className="text-sm text-stone" data-testid="admin-partners-empty">Nessuna richiesta Partner. Condividi otelya.app/partner.</div>}
      </div>
    </div>
  );
}
